import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { employeeApi, userApi, projectApi, companyApi, leaveApi, compoffApi } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { formatCurrency, calculateEmployerPF, employeePfLabel, formatWageCapSourceLabel, resolveSalaryWageCaps } from '@/lib/payrollCalc';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { toast } from 'sonner';
import { ArrowLeft, Edit, Phone, Mail, MapPin, Shield, FileText, KeyRound, TrendingUp, Download, Eye, MessageSquare, Printer } from 'lucide-react';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { CtcSlipModal } from '@/components/CtcSlipModal';

const normalizeCompOffAsLeave = (compoff) => {
  if (!compoff) return null;
  const id = compoff.compoffId || compoff.leaveId || compoff.id || compoff._id;
  return {
    ...compoff,
    leaveId: id,
    compoffId: id,
    type: 'CompOff',
    isCompOff: true,
  };
};

const normalizeLeaveList = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.leaves)) return value.leaves;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.data)) return value.data;
  return [];
};

const leaveStatusColor = (status) => {
  if (status === 'Approved') return 'bg-green-100 text-green-700 border-green-200';
  if (status === 'Pending') return 'bg-yellow-100 text-yellow-700 border-yellow-200';
  if (status === 'Cancelled') return 'bg-gray-100 text-gray-700 border-gray-200';
  return 'bg-red-100 text-red-700 border-red-200';
};

const IST_DATE_OPTS = { timeZone: 'Asia/Kolkata', day: '2-digit', month: '2-digit', year: 'numeric' };
const IST_TIME_OPTS = { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true };

const formatIstDateTime = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return {
    date: date.toLocaleDateString('en-IN', IST_DATE_OPTS),
    time: date.toLocaleTimeString('en-IN', IST_TIME_OPTS),
  };
};

const IstDateTimeCell = ({ value }) => {
  const formatted = formatIstDateTime(value);
  if (!formatted) return <span className="font-mono text-xs">-</span>;
  return (
    <div className="font-mono text-xs leading-tight">
      <div>{formatted.date}</div>
      <div className="text-muted-foreground">{formatted.time}</div>
    </div>
  );
};

const LeaveTypeBadge = ({ leave }) => (
  <Badge variant="outline">{leave?.type || '-'}</Badge>
);

function InfoRow({ label, value, mono = false }) {
  const resolvedValue = value === null || value === undefined || value === '' ? '-' : value;
  return (
    <div className="flex flex-col py-2">
      <span className="text-xs uppercase tracking-widest font-medium text-muted-foreground">{label}</span>
      <span className={`text-sm mt-0.5 break-words ${mono ? 'font-mono' : ''}`}>{resolvedValue}</span>
    </div>
  );
}

/** Returns full years from DOB (`YYYY-MM-DD` or ISO), or null. */
function getAgeFromDob(dob) {
  if (dob == null || dob === '') return null;
  const raw = String(dob).trim();
  if (!raw) return null;
  const isoDate = raw.includes('T') ? raw.slice(0, 10) : raw.slice(0, 10);
  const parts = isoDate.split('-');
  if (parts.length !== 3) return null;
  const y = parseInt(parts[0], 10);
  const mo = parseInt(parts[1], 10);
  const d = parseInt(parts[2], 10);
  if (!y || !mo || !d) return null;
  const birth = new Date(y, mo - 1, d);
  if (Number.isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) age -= 1;
  if (age < 0 || age > 150) return null;
  return age;
}

export default function EmployeeProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasAccess, user } = useAuth();

  const [emp, setEmp] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [salaryRevisions, setSalaryRevisions] = useState([]);
  const [existingUser, setExistingUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const [credOpen, setCredOpen] = useState(false);
  const [credForm, setCredForm] = useState({ email: '', password: '', phone: '' });
  const [otpSent, setOtpSent] = useState(false);
  const [otpValue, setOtpValue] = useState('');
  const [otpVerified, setOtpVerified] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [confirmCredOpen, setConfirmCredOpen] = useState(false);
  const [leaveHistory, setLeaveHistory] = useState([]);
  const [leaveHistoryLoading, setLeaveHistoryLoading] = useState(false);
  const [ctcPrintOpen, setCtcPrintOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [empData, allEmps, revisions, allProjects, allCompanies] = await Promise.all([
          employeeApi.getById(id),
          employeeApi.getAll(),
          employeeApi.getSalaryRevisions(id).catch(() => []),
          projectApi.getAll().catch(() => []),
          companyApi.getAll().catch(() => []),
        ]);
        setEmp(empData);
        setEmployees(allEmps);
        setSalaryRevisions(revisions);
        setProjects(Array.isArray(allProjects) ? allProjects : []);
        setCompanies(Array.isArray(allCompanies) ? allCompanies : []);

        // Check if user has credentials
        const userCheck = await userApi.check(id).catch(() => null);
        setExistingUser(userCheck);
      } catch (err) {
        console.error('Failed to fetch employee:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  useEffect(() => {
    if (!id) return;
    const fetchLeaveHistory = async () => {
      try {
        setLeaveHistoryLoading(true);
        const params = { employeeId: id };
        const [leaveResponse, compoffResponse] = await Promise.all([
          leaveApi.getAll(params).catch(() => []),
          compoffApi.getAll(params).catch(() => []),
        ]);
        const leaves = normalizeLeaveList(leaveResponse);
        const compoffs = normalizeLeaveList(compoffResponse)
          .map(normalizeCompOffAsLeave)
          .filter(Boolean);
        const merged = [...leaves, ...compoffs].sort((a, b) => {
          const aTime = new Date(a?.appliedOn || a?.createdAt || 0).getTime();
          const bTime = new Date(b?.appliedOn || b?.createdAt || 0).getTime();
          return bTime - aTime;
        });
        setLeaveHistory(merged);
      } catch (err) {
        console.error('Failed to fetch leave history:', err);
        setLeaveHistory([]);
      } finally {
        setLeaveHistoryLoading(false);
      }
    };
    fetchLeaveHistory();
  }, [id]);

  const sortedLeaveHistory = useMemo(
    () => [...leaveHistory].sort((a, b) => new Date(b?.appliedOn || 0) - new Date(a?.appliedOn || 0)),
    [leaveHistory]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-[#0047AB] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!emp) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <p className="text-muted-foreground">Employee not found</p>
        <Button variant="outline" onClick={() => navigate('/employees')} className="mt-4">Go Back</Button>
      </div>
    );
  }

  const manager = emp.reportingManager ? employees.find(e => e.empId === emp.reportingManager || e.id === emp.reportingManager) : null;
  const ageYears = getAgeFromDob(emp?.dob);

  const documents = emp.documents || [];
  const assignedProjects = Array.isArray(emp?.projects) ? emp.projects : [];
  const weeklyOffDays = Array.isArray(emp?.weeklyOffDays) ? emp.weeklyOffDays : [];
  const companyDisplayName = companies.find((company) =>
    company?.companyId === emp?.company || company?.name === emp?.company
  )?.name || emp?.company || '-';
  const projectDisplayNames = assignedProjects
    .map((projectId) => projects.find((project) => project?.projectId === projectId || project?.name === projectId)?.name)
    .filter(Boolean);

  const handleSendOTP = () => {
    if (!credForm.phone || credForm.phone.length < 10) {
      toast.error('Enter a valid phone number');
      return;
    }
    setOtpSent(true);
    toast.success(`OTP sent to ${credForm.phone} (Demo: use 123456)`);
  };

  const handleVerifyOTP = () => {
    if (otpValue === '123456') {
      setOtpVerified(true);
      toast.success('Phone verified successfully');
    } else {
      toast.error('Invalid OTP. Use 123456 for demo.');
    }
  };

  const handleCreateCredentialsClick = () => {
    if (!credForm.email || !credForm.password) {
      toast.error('Please fill email and password');
      return;
    }
    if (credForm.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setConfirmCredOpen(true);
  };

  const handleCreateCredentials = async () => {
    setConfirmCredOpen(false);
    try {
      if (existingUser) {
        await userApi.reset(emp.empId || emp.id, { email: credForm.email, password: credForm.password });
        toast.success('Credentials reset successfully');
      } else {
        await userApi.create({
          email: credForm.email,
          password: credForm.password,
          role: 'Employee',
          employeeId: emp.empId || emp.id,
          name: `${emp.firstName} ${emp.lastName}`,
        });
        toast.success('Login credentials created');
      }
      setExistingUser({ email: credForm.email });
      setCredOpen(false);
      setCredForm({ email: '', password: '', phone: '' });
      setOtpSent(false);
      setOtpValue('');
      setOtpVerified(false);
    } catch (err) {
      toast.error(err.message || 'Failed to manage credentials');
    }
  };

  const handleDownloadDoc = (doc) => {
    const link = document.createElement('a');
    link.href = doc.data;
    link.download = doc.fileName;
    link.click();
  };

  return (
    <div data-testid="employee-profile-page" className="space-y-3 animate-fadeIn">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="sm" onClick={() => navigate('/employees')} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
      </div>

      {/* Profile Header */}
      <Card className="border border-border shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="shrink-0">
              {emp.picture ? (
                <img
                  src={emp.picture}
                  alt={`${emp.firstName ?? ''} ${emp.lastName ?? ''}`.trim() || 'Profile'}
                  className="w-20 h-20 rounded-full object-cover border-2 border-border"
                />
              ) : (
                <div className="w-20 h-20 rounded-full bg-[#0047AB]/10 flex items-center justify-center text-2xl font-bold text-[#0047AB] font-[Manrope]">
                  {emp.firstName?.charAt(0)}
                  {emp.lastName?.charAt(0)}
                </div>
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold tracking-tight font-[Manrope]">{emp.firstName} {emp.lastName}</h1>
                <Badge
                  className={
                    emp.status === 'Active'
                      ? 'bg-green-100 text-green-700 border-green-200'
                      : emp.status === 'Draft'
                        ? 'bg-yellow-100 text-yellow-700 border-yellow-200'
                        : 'bg-red-100 text-red-700 border-red-200'
                  }
                >
                  {emp.status}
                </Badge>
                {existingUser && <Badge variant="outline" className="text-xs">Has Login</Badge>}
              </div>
              <p className="text-muted-foreground text-sm mt-1">
                {[emp.designation, emp.department].filter(Boolean).join(' - ')}
              </p>
              <div className="flex flex-wrap gap-4 mt-2">
                {emp.phone && <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><Phone className="w-3 h-3" />{emp.phone}</span>}
                {emp.email && <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><Mail className="w-3 h-3" />{emp.email}</span>}
                {emp.workLocation && <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><MapPin className="w-3 h-3" />{emp.workLocation}</span>}
                {(emp.empId || emp.id) && <span className="flex items-center gap-1.5 text-xs text-muted-foreground font-mono"><Shield className="w-3 h-3" />{emp.empId || emp.id}</span>}
              </div>
            </div>
            {hasAccess('Manager') && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setCredForm({ email: existingUser?.email || emp.email, password: '', phone: emp.phone });
                    setCredOpen(true);
                  }}
                  data-testid="manage-credentials-btn"
                >
                  <KeyRound className="w-4 h-4 mr-2" />
                  {existingUser ? 'Reset Login' : 'Create Login'}
                </Button>
                <Button onClick={() => navigate(`/employees/edit/${emp.empId || emp.id}`)} className="bg-[#0047AB] hover:bg-[#003d96] text-white" data-testid="edit-employee-btn">
                  <Edit className="w-4 h-4 mr-2" /> Edit
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="personal" className="space-y-4">
        <TabsList>
          <TabsTrigger value="personal">Personal</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents ({documents.length})</TabsTrigger>
          <TabsTrigger value="salary">Salary</TabsTrigger>
          <TabsTrigger value="leaves">Leave Balance</TabsTrigger>
        </TabsList>

        <TabsContent value="personal" className="space-y-3">
          <Card className="border border-border shadow-sm">
            <CardHeader><CardTitle className="text-base font-[Manrope]">Personal Information</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-x-6">
              <InfoRow label="Employee ID" value={emp?.empId || emp?.id} mono />
              <InfoRow label="Father Name" value={emp?.fatherName} />
              <InfoRow label="Date of Birth" value={emp.dob} mono />
              <InfoRow label="Age" value={ageYears == null ? null : `${ageYears} `} mono />
              <InfoRow label="Gender" value={emp.gender} />
              <InfoRow label="Marital Status" value={emp.maritalStatus} />
              <InfoRow label="Phone" value={emp.phone} mono />
              <InfoRow label="Email" value={emp.email} />
              <InfoRow label="Address" value={emp.address} />
              <InfoRow label="Emergency Contact" value={emp.emergencyContact?.name} />
              <InfoRow label="Emergency Relation" value={emp.emergencyContact?.relation} />
              <InfoRow label="Emergency Phone" value={emp.emergencyContact?.phone} mono />
            </CardContent>
          </Card>

          <Card className="border border-border shadow-sm">
            <CardHeader><CardTitle className="text-base font-[Manrope]">Job Information</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-x-6">
              <InfoRow label="Company" value={companyDisplayName} />
              <InfoRow label="Department" value={emp.department} />
              <InfoRow label="Designation" value={emp.designation} />
              <InfoRow label="Grade" value={emp.grade} />
              <InfoRow label="Reporting Manager" value={manager ? `${manager.firstName} ${manager.lastName}` : 'None'} />
              <InfoRow label="Employment Type" value={emp.employmentType} />
              <InfoRow label="Joining Date" value={emp.joiningDate} mono />
              {emp?.status === 'Inactive' && (
                <InfoRow label="Inactive Date" value={emp?.inactiveDate || '-'} mono />
              )}
              <InfoRow label="Shift Start" value={emp?.shiftStart} mono />
              <InfoRow label="Shift End" value={emp?.shiftEnd} mono />
              <InfoRow label="Monthly Leave Allowance" value={emp?.monthlyLeaveAllowance} mono />
              <InfoRow label="Weekly Off Days" value={weeklyOffDays.length > 0 ? weeklyOffDays.join(', ') : '-'} />
              <InfoRow label="Show Holidays" value={emp?.showHoliday === true ? 'Yes' : 'No'} />
              <InfoRow label="Work Location" value={emp.workLocation} />
              <InfoRow label="Status" value={emp.status} />
              <InfoRow label="System Role" value={emp?.role} />
              {projectDisplayNames.length > 0 && (
                <div className="col-span-full flex flex-col py-2">
                  <span className="text-xs uppercase tracking-widest font-medium text-muted-foreground">Projects</span>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {projectDisplayNames.map((projectName) => (
                      <Badge key={projectName} variant="outline" className="text-xs">
                        {projectName}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border border-border shadow-sm">
            <CardHeader><CardTitle className="text-base font-[Manrope]">Statutory & Bank Details</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-x-6">
              <InfoRow label="PAN Number" value={emp.panNumber} mono />
              <InfoRow label="Aadhaar Number" value={emp.aadhaarNumber} mono />
              <InfoRow label="PF Number" value={emp.pfNumber} mono />
              <InfoRow label="ESI Number" value={emp.esiNumber} mono />
              <InfoRow label="UAN Number" value={emp.uanNumber} mono />
              <InfoRow label="Bank Name" value={emp.bankName} />
              <InfoRow label="Account No." value={emp.bankAccount} mono />
              <InfoRow label="IFSC Code" value={emp.ifscCode} mono />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents">
          <Card className="border border-border shadow-sm">
            <CardHeader>
              <CardTitle className="text-base font-[Manrope] flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#0047AB]" />
                Uploaded Documents
              </CardTitle>
            </CardHeader>
            <CardContent>
              {documents.length > 0 ? (
                <div className="space-y-2">
                  {documents.map(doc => {
                    const docKey = doc?.docId || doc?.id || doc?.fileName;
                    return (
                    <div key={docKey} className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-accent/30 transition-colors" data-testid={`doc-${docKey}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-[#0047AB]/10 flex items-center justify-center">
                          <FileText className="w-5 h-5 text-[#0047AB]" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">{doc.type}</p>
                          <p className="text-xs text-muted-foreground">{doc.fileName} ({doc.fileSize})</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">{new Date(doc.uploadedAt).toLocaleDateString()}</Badge>
                        {doc.data && doc.mimeType?.startsWith('image/') && (
                          <Button size="sm" variant="ghost" className="h-7" onClick={() => setPreviewDoc(doc)} data-testid={`preview-doc-${docKey}`}>
                            <Eye className="w-3 h-3 mr-1" /> View
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="h-7" onClick={() => handleDownloadDoc(doc)} data-testid={`download-doc-${docKey}`}>
                          <Download className="w-3 h-3 mr-1" /> Download
                        </Button>
                      </div>
                    </div>
                  )})}
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No documents uploaded</p>
                  <p className="text-xs mt-1">Go to Edit Employee to upload documents</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="salary">
          {(() => {
            const sal = emp.salary || {};
            const basic = Number(sal.basic) || 0;
            const hra = Number(sal.hra) || 0;
            const specialAllowance = Number(sal.specialAllowance) || 0;
            const conveyance = Number(sal.conveyance) || 0;
            const telephoneExpense = Number(sal.telephoneExpense) || 0;
            const mediclaim = Number(sal.mediclaim) || 0;
            const tds = Number(sal.tds) || 0;
            const epf = Number(sal.epf) || 0;
            const esic = Number(sal.esic) || 0;
            const bonus = Number(sal.bonus) || 0;
            const wageCaps = resolveSalaryWageCaps(sal, {
              assignedProjectIds: emp?.projects,
              projects,
            });
            const employerPF =
              sal.employerPF != null ? Number(sal.employerPF) || 0 : calculateEmployerPF(basic, wageCaps.pfLimit);
            const employerESIC = Number(sal.employerESIC) || 0;
            const gratuity = Number(sal.gratuity) || 0;
            const employerPfLimitEnabled = sal.employerPfLimit != null && sal.employerPfLimit !== '';
            const esicThresholdEnabled = sal.esicThreshold != null && sal.esicThreshold !== '';
            const employerPfLimitOverride = employerPfLimitEnabled ? Number(sal.employerPfLimit) || 0 : 0;
            const esicThresholdOverride = esicThresholdEnabled ? Number(sal.esicThreshold) || 0 : 0;
            const salGross = basic + hra + specialAllowance;
            const totalReimb = conveyance;
            const totalDed = epf + esic + tds;
            const inHand = salGross + totalReimb - totalDed;
            const employerTotal = employerPF + employerESIC + gratuity + mediclaim + telephoneExpense;
            const ctc = salGross + totalReimb + bonus + employerTotal;
            const extra = sal.extraReimbursement || {};
            const extraFreq = extra.extraReimbursementFrequency === 'Yearly' ? 'Yearly' : 'Monthly';
            const extraAmt = Number(extra.extraReimbursementAmount) || 0;
            const monthlyExtra = extraFreq === 'Yearly' ? Math.round(extraAmt / 12) : extraAmt;
            const annualExtra = extraFreq === 'Yearly' ? extraAmt : extraAmt * 12;
            const inHandAfterExtra =
              Number(extra.inHandSalaryAfterExtraReimbursement) || inHand + monthlyExtra;
            const inHandAfterExtraAnnual =
              Number(extra.inHandSalaryAfterExtraReimbursementPerAnnum) || inHand * 12 + annualExtra;
            const ctcAfterExtra =
              Number(extra.totalCtcAfterExtraReimbursement) || ctc + monthlyExtra;
            const ctcAfterExtraAnnual =
              Number(extra.totalCtcAfterExtraReimbursementPerAnnum) || ctc * 12 + annualExtra;
            const fmt = (v) => Number(v || 0).toLocaleString('en-IN');
            const ann = (v) => Number(v || 0) * 12;

            const SectionHeader = ({ label, code }) => (
              <tr className="bg-[#0047AB]/5">
                <td colSpan={3} className="py-2 px-3 text-xs font-bold uppercase tracking-widest text-[#0047AB] border-b border-[#0047AB]/20">
                  {label} {code && <span className="text-muted-foreground">({code})</span>}
                </td>
              </tr>
            );
            const Row = ({ label, monthly, annual, highlight, bold, hideAnnual = false, off = false }) => (
              <tr className={`${highlight ? 'bg-muted/60' : ''} ${bold ? 'font-semibold' : ''}`}>
                <td className="py-2 px-3 text-sm border-b border-border">{label}</td>
                <td className="py-2 px-3 border-b border-border text-right">
                  {off ? (
                    <span className="text-sm text-muted-foreground">Off</span>
                  ) : (
                    <span className={`font-mono text-sm ${bold ? 'text-[#0047AB]' : ''}`}>&#8377;{fmt(monthly)}</span>
                  )}
                </td>
                <td className="py-2 px-3 border-b border-border text-right">
                  {hideAnnual ? (
                    <span className="font-mono text-sm text-muted-foreground">—</span>
                  ) : (
                    <span className={`font-mono text-sm ${bold ? 'text-[#0047AB]' : ''}`}>&#8377;{fmt(annual)}</span>
                  )}
                </td>
              </tr>
            );

            return (
              <Card className="border border-border shadow-sm">
                <CardHeader className="pb-2 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between space-y-0">
                  <div>
                    <CardTitle className="text-base font-[Manrope]">Salary CTC Structure</CardTitle>
                    <p className="text-xs text-muted-foreground">Monthly and annual salary breakdown</p>
                    {wageCaps.projectLabel && (
                      <Badge
                        variant="outline"
                        className="mt-2 w-fit max-w-full whitespace-normal text-left font-normal text-[#0047AB] border-[#0047AB]/30 bg-[#0047AB]/5"
                        data-testid="project-caps-label"
                      >
                        PF/ESIC calculated using project: {wageCaps.projectLabel}
                      </Badge>
                    )}
                  </div>
                  <Button
                    size="sm"
                    onClick={() => setCtcPrintOpen(true)}
                    data-testid="print-ctc-open-btn"
                    className="shrink-0 self-start bg-[#0047AB] hover:bg-[#003d96] text-white"
                  >
                    <Printer className="w-4 h-4 mr-2" /> Print CTC
                  </Button>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-muted/80">
                          <th className="py-2 px-3 text-left text-xs uppercase tracking-widest font-bold text-muted-foreground border-b-2 border-border w-[50%]">Salary Component</th>
                          <th className="py-2 px-3 text-right text-xs uppercase tracking-widest font-bold text-muted-foreground border-b-2 border-border w-[25%]">Per Month</th>
                          <th className="py-2 px-3 text-right text-xs uppercase tracking-widest font-bold text-muted-foreground border-b-2 border-border w-[25%]">Per Annum</th>
                        </tr>
                      </thead>
                      <tbody>
                        <SectionHeader label="Earnings" />
                        <Row label="Basic" monthly={basic} annual={ann(basic)} />
                        <Row label="House Rent Allowance" monthly={hra} annual={ann(hra)} />
                        <Row label="Special Allowance" monthly={specialAllowance} annual={ann(specialAllowance)} />
                        <Row label="Gross Salary (A)" monthly={salGross} annual={ann(salGross)} highlight bold />

                        <SectionHeader label="Statutory Caps" />
                        <Row
                          label="Employer PF Limit (employee override)"
                          monthly={employerPfLimitOverride}
                          annual={employerPfLimitOverride}
                          hideAnnual
                          off={!employerPfLimitEnabled}
                        />
                        <Row
                          label="ESIC Threshold (employee override)"
                          monthly={esicThresholdOverride}
                          annual={esicThresholdOverride}
                          hideAnnual
                          off={!esicThresholdEnabled}
                        />
                        <Row
                          label={`Applied Employer PF Limit (${formatWageCapSourceLabel(wageCaps.pfSource, wageCaps.projectLabel)})`}
                          monthly={wageCaps.pfLimit}
                          annual={wageCaps.pfLimit}
                          hideAnnual
                        />
                        <Row
                          label={`Applied ESIC Threshold (${formatWageCapSourceLabel(wageCaps.esicSource, wageCaps.projectLabel)})`}
                          monthly={wageCaps.esicThreshold}
                          annual={wageCaps.esicThreshold}
                          hideAnnual
                        />

                        <SectionHeader label="Reimbursements" code="B" />
                        <Row label="Conveyance Reimbursements (Driver, Petrol)" monthly={conveyance} annual={ann(conveyance)} />
                        <Row label="Total Reimbursements (B)" monthly={totalReimb} annual={ann(totalReimb)} highlight bold />
                        <Row
                          label={`Extra Reimbursement (${extraFreq})`}
                          monthly={monthlyExtra}
                          annual={annualExtra}
                        />

                        <SectionHeader label="Employee Deductions" code="C" />
                        <Row label={employeePfLabel(sal.applyPfCeilingToEmployee)} monthly={epf} annual={ann(epf)} />
                        <Row label="ESIC (0.75% of Gross)" monthly={esic} annual={ann(esic)} />
                        <Row label="TDS" monthly={tds} annual={ann(tds)} />
                        <Row label="Total Deductions (C)" monthly={totalDed} annual={ann(totalDed)} highlight bold />

                        <tr className="bg-[#10b981]/10">
                          <td className="py-3 px-3 text-sm font-bold border-b border-[#10b981]/30">In Hand Salary (A + B - C)</td>
                          <td className="py-3 px-3 text-right font-mono text-sm font-bold text-[#10b981] border-b border-[#10b981]/30">&#8377;{fmt(inHand)}</td>
                          <td className="py-3 px-3 text-right font-mono text-sm font-bold text-[#10b981] border-b border-[#10b981]/30">&#8377;{fmt(ann(inHand))}</td>
                        </tr>
                        <tr className="bg-[#10b981]/5">
                          <td className="py-2 px-3 text-sm border-b-2 border-[#10b981]/30">In Hand + Extra Reimbursement</td>
                          <td className="py-2 px-3 text-right font-mono text-sm font-semibold text-[#10b981] border-b-2 border-[#10b981]/30">&#8377;{fmt(inHandAfterExtra)}</td>
                          <td className="py-2 px-3 text-right font-mono text-sm font-semibold text-[#10b981] border-b-2 border-[#10b981]/30">&#8377;{fmt(inHandAfterExtraAnnual)}</td>
                        </tr>

                        <SectionHeader label="Bonus" code="D" />
                        <Row label="Bonus (8.33% of Basic) (Paid Annually)" monthly={bonus} annual={ann(bonus)} />

                        <SectionHeader label="Employer Contributions" code="E" />
                        <Row label="Employer PF Contribution (13% of Basic)" monthly={employerPF} annual={ann(employerPF)} />
                        <Row label="ESIC (3.25% of Gross)" monthly={employerESIC} annual={ann(employerESIC)} />
                        <Row label="Telephone Expense" monthly={telephoneExpense} annual={ann(telephoneExpense)} />
                        <Row label="Mediclaim Insurance Premium" monthly={mediclaim} annual={ann(mediclaim)} />
                        <Row label="Gratuity (4.81% of Basic)" monthly={gratuity} annual={ann(gratuity)} />
                        <Row label="Employer Contributions (E)" monthly={employerTotal} annual={ann(employerTotal)} highlight bold />

                        <tr className="bg-[#0047AB]/10">
                          <td className="py-3 px-3 text-sm font-bold border-b border-[#0047AB]/30">CTC (A + B + D + E)</td>
                          <td className="py-3 px-3 text-right font-mono text-base font-bold text-[#0047AB] border-b border-[#0047AB]/30">&#8377;{fmt(ctc)}</td>
                          <td className="py-3 px-3 text-right font-mono text-base font-bold text-[#0047AB] border-b border-[#0047AB]/30">&#8377;{fmt(ann(ctc))}</td>
                        </tr>
                        <tr className="bg-[#0047AB]/5">
                          <td className="py-2 px-3 text-sm">CTC + Extra Reimbursement</td>
                          <td className="py-2 px-3 text-right font-mono text-sm font-bold text-[#0047AB]">&#8377;{fmt(ctcAfterExtra)}</td>
                          <td className="py-2 px-3 text-right font-mono text-sm font-bold text-[#0047AB]">&#8377;{fmt(ctcAfterExtraAnnual)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            );
          })()}

          {/* Salary Revision History */}
          <Card className="border border-border shadow-sm mt-4">
            <CardHeader>
              <CardTitle className="text-base font-[Manrope] flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-[#0047AB]" />
                Salary Revision History
              </CardTitle>
            </CardHeader>
            <CardContent>
              {salaryRevisions.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="text-xs uppercase tracking-widest">Effective Date</TableHead>
                      <TableHead className="text-xs uppercase tracking-widest text-right">Previous CTC</TableHead>
                      <TableHead className="text-xs uppercase tracking-widest text-right">Revised CTC</TableHead>
                      <TableHead className="text-xs uppercase tracking-widest text-center">Change</TableHead>
                      <TableHead className="text-xs uppercase tracking-widest">Revised By</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {salaryRevisions.map(rev => (
                      <TableRow key={rev.revId || rev.id} data-testid={`revision-${rev.revId || rev.id}`}>
                        <TableCell className="font-mono text-xs">{rev.effectiveDate}</TableCell>
                        <TableCell className="text-right font-mono text-xs">{formatCurrency(rev.previousGross)}/mo</TableCell>
                        <TableCell className="text-right font-mono text-xs font-bold">{formatCurrency(rev.newGross)}/mo</TableCell>
                        <TableCell className="text-center">
                          <Badge className={parseFloat(rev.changePercent) >= 0 ? 'bg-green-100 text-green-700 border-green-200' : 'bg-red-100 text-red-700 border-red-200'}>
                            {parseFloat(rev.changePercent) >= 0 ? '+' : ''}{rev.changePercent}%
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">{rev.revisedBy}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-6">No salary revisions recorded</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="leaves" className="space-y-4">
          <Card className="border border-border shadow-sm">
            <CardHeader><CardTitle className="text-base font-[Manrope]">Leave Balance</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  { key: 'EL', label: 'Earned Leave', color: 'text-[#0047AB]' },
                  { key: 'CL', label: 'Casual Leave', color: 'text-[#6366f1]' },
                  { key: 'ML', label: 'Medical Leave', color: 'text-[#10b981]' },
                  { key: 'LOP', label: 'LOP Taken', color: 'text-[#ef4444]' },
                ].map(({ key, label, color }) => (
                  <div key={key} className="p-4 rounded-lg border border-border text-center">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground">{label}</p>
                    <p className={`text-2xl font-bold font-mono mt-1 ${color}`}>{emp?.leaveBalance?.[key] ?? 0}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border border-border shadow-sm" data-testid="employee-leave-history-card">
            <CardHeader>
              <CardTitle className="text-base font-[Manrope]">Leave History</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="text-xs uppercase tracking-widest">Type</TableHead>
                      <TableHead className="text-xs uppercase tracking-widest">From</TableHead>
                      <TableHead className="text-xs uppercase tracking-widest">To</TableHead>
                      <TableHead className="text-xs uppercase tracking-widest">Reason</TableHead>
                      <TableHead className="text-xs uppercase tracking-widest">Approved By</TableHead>
                      <TableHead className="text-xs uppercase tracking-widest">Approved On</TableHead>
                      <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
                      <TableHead className="text-xs uppercase tracking-widest">Applied</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leaveHistoryLoading && (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-6 text-muted-foreground">
                          Loading leave history...
                        </TableCell>
                      </TableRow>
                    )}
                    {!leaveHistoryLoading && sortedLeaveHistory.map((leave) => (
                      <TableRow key={leave?.leaveId || leave?.id || leave?.compoffId} data-testid={`profile-leave-row-${leave?.leaveId || leave?.id}`}>
                        <TableCell><LeaveTypeBadge leave={leave} /></TableCell>
                        <TableCell className="font-mono text-xs">{leave?.startDate || '-'}</TableCell>
                        <TableCell className="font-mono text-xs">{leave?.endDate || '-'}</TableCell>
                        <TableCell className="text-sm max-w-[160px]">
                          {leave?.reason ? (
                            <HoverCard openDelay={150} closeDelay={100}>
                              <HoverCardTrigger asChild>
                                <button
                                  type="button"
                                  className="max-w-full truncate cursor-help outline-none text-left"
                                  aria-label="View full reason"
                                >
                                  {leave.reason}
                                </button>
                              </HoverCardTrigger>
                              <HoverCardContent align="start" className="w-72 p-3 space-y-1.5">
                                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Reason</p>
                                <p className="text-sm leading-relaxed whitespace-pre-wrap">{leave.reason}</p>
                              </HoverCardContent>
                            </HoverCard>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell className="text-sm">{leave?.reviewedByName ?? leave?.reviewedBy ?? '-'}</TableCell>
                        <TableCell><IstDateTimeCell value={leave?.reviewedOn} /></TableCell>
                        <TableCell>
                          {leave?.reviewComment ? (
                            <HoverCard openDelay={150} closeDelay={100}>
                              <HoverCardTrigger asChild>
                                <button
                                  type="button"
                                  className="inline-flex items-center gap-1 cursor-help outline-none"
                                  aria-label={`Status ${leave.status}, view review comment`}
                                >
                                  <Badge className={leaveStatusColor(leave.status)}>{leave.status}</Badge>
                                  <MessageSquare className="w-3 h-3 text-muted-foreground" />
                                </button>
                              </HoverCardTrigger>
                              <HoverCardContent align="start" className="w-72 p-3 space-y-1.5">
                                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Review Comment</p>
                                <p className="text-sm leading-relaxed">{leave.reviewComment}</p>
                              </HoverCardContent>
                            </HoverCard>
                          ) : (
                            <Badge className={leaveStatusColor(leave?.status)}>{leave?.status || '-'}</Badge>
                          )}
                        </TableCell>
                        <TableCell><IstDateTimeCell value={leave?.appliedOn} /></TableCell>
                      </TableRow>
                    ))}
                    {!leaveHistoryLoading && sortedLeaveHistory.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                          No leave records
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Credential Management Dialog */}
      <Dialog open={credOpen} onOpenChange={(open) => { setCredOpen(open); if (!open) { setOtpSent(false); setOtpValue(''); setOtpVerified(false); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">{existingUser ? 'Reset Login Credentials' : 'Create Login Credentials'}</DialogTitle>
            <DialogDescription>
              {existingUser ? `Reset credentials for ${emp.firstName} ${emp.lastName}` : `Create login access for ${emp.firstName} ${emp.lastName}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Phone OTP Verification */}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Phone Verification (Optional)</Label>
              <div className="flex gap-2">
                <Input
                  value={credForm.phone}
                  onChange={e => setCredForm(p => ({ ...p, phone: e.target.value }))}
                  placeholder="Phone number"
                  disabled={otpVerified}
                  data-testid="cred-phone-input"
                />
                {!otpSent ? (
                  <Button variant="outline" onClick={handleSendOTP} disabled={otpVerified} data-testid="send-otp-btn">Send OTP</Button>
                ) : !otpVerified ? (
                  <div className="flex gap-1">
                    <Input
                      value={otpValue}
                      onChange={e => setOtpValue(e.target.value)}
                      placeholder="OTP"
                      className="w-24"
                      data-testid="otp-input"
                    />
                    <Button variant="outline" onClick={handleVerifyOTP} data-testid="verify-otp-btn">Verify</Button>
                  </div>
                ) : (
                  <Badge className="bg-green-100 text-green-700 border-green-200 self-center">Verified</Badge>
                )}
              </div>
              {otpSent && !otpVerified && <p className="text-xs text-muted-foreground">Demo OTP: 123456</p>}
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Login Email</Label>
              <Input
                value={credForm.email}
                onChange={e => setCredForm(p => ({ ...p, email: e.target.value }))}
                placeholder="employee@nexhr.com"
                data-testid="cred-email-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">{existingUser ? 'New Password' : 'Password'}</Label>
              <Input
                type="password"
                value={credForm.password}
                onChange={e => setCredForm(p => ({ ...p, password: e.target.value }))}
                placeholder="Min 6 characters"
                data-testid="cred-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCredOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateCredentialsClick} className="bg-[#0047AB] hover:bg-[#003d96] text-white" data-testid="save-credentials-btn">
              <KeyRound className="w-4 h-4 mr-2" />
              {existingUser ? 'Reset Credentials' : 'Create Credentials'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmCredOpen}
        onOpenChange={setConfirmCredOpen}
        title={existingUser ? 'Reset Credentials?' : 'Create Credentials?'}
        description={
          existingUser
            ? `Reset login credentials for ${emp?.firstName || 'this employee'}? They will need the new password to sign in.`
            : `Create login access for ${emp?.firstName || 'this employee'} with email ${credForm.email}?`
        }
        confirmLabel={existingUser ? 'Reset Credentials' : 'Create Credentials'}
        variant="warning"
        onConfirm={handleCreateCredentials}
        data-testid="employee-cred-confirm"
      />

      {/* Document Preview Dialog */}
      <Dialog open={!!previewDoc} onOpenChange={() => setPreviewDoc(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">{previewDoc?.type}</DialogTitle>
            <DialogDescription>{previewDoc?.fileName}</DialogDescription>
          </DialogHeader>
          {previewDoc?.data && (
            <img src={previewDoc.data} alt={previewDoc.type} className="w-full rounded-lg border" />
          )}
        </DialogContent>
      </Dialog>

      <CtcSlipModal
        open={ctcPrintOpen}
        onOpenChange={setCtcPrintOpen}
        employee={emp}
        projects={projects}
      />
    </div>
  );
}
